import React from 'react';
import RoleFilter from './RoleFilter';
import MobileEmployeeSearchHeader from '../mobile/MobileEmployeeSearchHeader';
import '../../../../components/clients/search/ClientSearchHeader.css';
import '../mobile/EmployeeSearchHeaderMobile.css';
import ManagementFilterSkeleton from '../../../../shared/management-layout/ManagementFilterSkeleton';
import { SkeletonBlock } from '../../../../components/loading/skeletons';

const IconMail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const IconClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const IconExport = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const IconSearch = () => (
  <svg className="clientSearchInputIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const IconGallery = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const IconTable = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const EmployeeSearchHeader = ({
  searchTerm,
  onSearchChange,
  onInviteEmployee,
  canInviteEmployee,
  onOpenPendingInvites,
  onManagePermissions,
  canOpenPermissions,
  onExport,
  canExport,
  onRefresh,
  isRefreshing,
  viewMode,
  onViewModeChange,
  teamMembersCountDisplay = '',
  mobileSeatPillLabel = '',
  roles = [],
  selectedRoleId = 'all',
  onRoleClick,
  hideToolbarActions = false,
  onOpenMobileRoleFilter,
  onOpenMobileSort,
  isLoading = false,
}) => {
  const useMobileFilterBar =
    hideToolbarActions && typeof onOpenMobileRoleFilter === 'function' && typeof onOpenMobileSort === 'function';

  const selectedRoleLabel =
    selectedRoleId === 'all'
      ? ''
      : roles.find((r) => r.id === selectedRoleId)?.label ?? selectedRoleId;

  return (
    <div
      className={
        'app-table-wrap clientSearchHeader' +
        (hideToolbarActions ? ' employeeSearchHeader--compactTopActions' : '')
      }
    >
      <header className="clientSearchHeaderBar" aria-label="Employee list">
        <div className="clientSearchHeaderMeta">
          {isLoading ? (
            <SkeletonBlock variant="pill" width={96} height={22} />
          ) : teamMembersCountDisplay ? (
            <span className="clientSearchHeaderPill">{teamMembersCountDisplay}</span>
          ) : null}
        </div>
      </header>

      <div className="clientSearchHeaderBody">
        {useMobileFilterBar ? (
          <MobileEmployeeSearchHeader
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            onFilterClick={onOpenMobileRoleFilter}
            onSortClick={onOpenMobileSort}
            countPillLabel={mobileSeatPillLabel}
            hasRoleFilter={selectedRoleId !== 'all'}
            selectedRoleLabel={selectedRoleLabel}
            onClearRoleFilter={() => onRoleClick('all')}
          />
        ) : isLoading ? (
          <ManagementFilterSkeleton variant="employee" label="Loading employee filters" />
        ) : (
          <>
            <div className="clientSearchHeaderToolbar">
              <div className="managementFilterSection">
                <p className="managementFilterSectionLabel">Search</p>
                <div className="clientSearchInputWrapper">
                  <IconSearch />
                  <input
                    className="clientSearchInput"
                    type="search"
                    placeholder="Search by name or email…"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    aria-label="Search employees"
                  />
                </div>
              </div>

              <div className="managementFilterSection">
                <p className="managementFilterSectionLabel">View</p>
                <div className="clientSearchViewToggle" role="group" aria-label="View mode">
                  <button
                    type="button"
                    className={`clientSearchViewToggleBtn ${viewMode === 'gallery' ? 'active' : ''}`}
                    onClick={() => onViewModeChange('gallery')}
                    title="Gallery view"
                    aria-pressed={viewMode === 'gallery'}
                  >
                    <IconGallery />
                    <span className="clientSearchViewToggleLabel">Gallery</span>
                  </button>
                  <button
                    type="button"
                    className={`clientSearchViewToggleBtn ${viewMode === 'table' ? 'active' : ''}`}
                    onClick={() => onViewModeChange('table')}
                    title="Table view"
                    aria-pressed={viewMode === 'table'}
                  >
                    <IconTable />
                    <span className="clientSearchViewToggleLabel">Table</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="managementFilterSection">
              <p className="managementFilterSectionLabel">Role</p>
              <RoleFilter roles={roles} selectedRoleId={selectedRoleId} onRoleClick={onRoleClick} />
            </div>

            <div className="managementFilterSection">
              <p className="managementFilterSectionLabel">Actions</p>
              <div className="clientSearchHeaderActions">
                <button
                  type="button"
                  className="clientSearchHeaderBtn"
                  onClick={onInviteEmployee}
                  disabled={!canInviteEmployee}
                >
                  <IconMail />
                  Invite
                </button>
                <button
                  type="button"
                  className="clientSearchHeaderBtn secondary"
                  onClick={onOpenPendingInvites}
                >
                  <IconClock />
                  Pending Invites
                </button>
                <button
                  type="button"
                  className="clientSearchHeaderBtn secondary"
                  onClick={onManagePermissions}
                  disabled={!canOpenPermissions}
                >
                  <IconShield />
                  Permissions
                </button>
                <span className="clientSearchHeaderActionsSpacer" />
                <button
                  type="button"
                  className="clientSearchHeaderBtn ghost"
                  onClick={onExport}
                  disabled={!canExport}
                >
                  <IconExport />
                  Export
                </button>
                <button
                  type="button"
                  className={`clientSearchHeaderBtn ghost refresh ${isRefreshing ? 'refreshing' : ''}`}
                  onClick={onRefresh}
                  title="Refresh"
                  aria-label="Refresh employees"
                >
                  <IconRefresh />
                  Refresh
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeSearchHeader;
